import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const featured = [
  {
    slug: 'str-enterprises',
    category: 'Culture review',
    client: 'STR Enterprises',
    headline: 'Protecting what works as a family business grows',
    sector: 'Hospitality',
    summary: 'Kalobrate listened to employees across the business, identified what should be protected as the company grew and helped leaders turn family values into practical decisions.',
  },
  {
    slug: 'dynatech-renewables',
    category: 'Culture by design',
    client: 'Dynatech Renewables',
    headline: 'Building the right culture before rapid growth',
    sector: 'Wind energy',
    summary: 'Kalobrate helped the founders define the values that would guide leadership, recruitment, onboarding and the way technicians were supported.',
  },
  {
    slug: 'bel-engineering',
    category: 'Workforce learning',
    client: 'BEL Engineering',
    headline: 'Making workplace learning relevant to an engineering workforce',
    sector: 'Manufacturing and engineering',
    summary: 'Kalobrate delivered practical learning across shifts and roles, making subjects such as sleep, stress and men\'s mental health relevant to an engineering workforce.',
  },
];

const FeaturedResultsSection = () => {
  return (
    <section className="py-20 md:py-28 bg-gray-50" data-testid="featured-results-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12" {...fadeInUp}>
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-carbon tracking-tight">
              WORK GROUNDED IN <span className="yellow-highlight text-carbon">REAL</span> ORGANISATIONS
            </h2>
          </div>
          <Link to="/results" className="text-sm font-semibold text-carbon hover:text-golden transition-colors inline-flex items-center flex-shrink-0">
            View all results <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {featured.map((cs, i) => (
            <motion.div
              key={cs.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Link
                to={`/results/${cs.slug}`}
                className="block bg-white border border-gray-100 p-6 group hover-lift h-full"
                data-testid={`featured-card-${cs.slug}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-golden/15 text-carbon text-xs font-semibold px-3 py-1">{cs.category}</span>
                  <span className="text-xs text-carbon/40">{cs.sector}</span>
                </div>
                <h3 className="text-base font-heading font-semibold text-carbon mb-1 group-hover:text-golden transition-colors">{cs.headline}</h3>
                <p className="text-xs text-carbon/50 mb-3">{cs.client}</p>
                <p className="text-sm text-carbon/65 leading-relaxed mb-4">{cs.summary}</p>
                <span className="inline-flex items-center text-sm font-semibold text-carbon group-hover:text-golden transition-colors">
                  View case study <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedResultsSection;
